import dayjs from "@shared/dayjs";

import type { DateRange, PreviousYearSegmentValue } from "./types";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

const MONTH_INDEX: Readonly<Record<Extract<PreviousYearSegmentValue, "january"
  | "february" | "march" | "april" | "may" | "june" | "july" | "august"
  | "september" | "october" | "november" | "december">, number>> = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
};

const QUARTER_START_MONTH: Readonly<Record<"q1" | "q2" | "q3" | "q4", number>> = {
  q1: 0,
  q2: 3,
  q3: 6,
  q4: 9,
};

const toIso = (date: dayjs.Dayjs): string => date.format(ISO_DATE_FORMAT);

const isQuarter = (value: PreviousYearSegmentValue): value is "q1" | "q2" | "q3" | "q4" =>
  value === "q1" || value === "q2" || value === "q3" || value === "q4";

/**
 * Resolve a previous-year segment + year to a concrete `{ startAt, endAt }`
 * range. Used in tandem with `getPreviousYearOptions`.
 */
export const resolvePreviousYearRange = (
  year: number,
  segment: PreviousYearSegmentValue,
): DateRange => {
  if (isQuarter(segment)) {
    const startMonth = QUARTER_START_MONTH[segment];
    const base = dayjs(new Date(year, startMonth, 1));
    return { startAt: toIso(base.startOf("quarter")), endAt: toIso(base.endOf("quarter")) };
  }

  const monthIndex = MONTH_INDEX[segment];
  const base = dayjs(new Date(year, monthIndex, 1));
  return { startAt: toIso(base.startOf("month")), endAt: toIso(base.endOf("month")) };
};
